import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import cx from 'classnames'
import { addDisplayName, addProps, flowMax } from 'ad-hok'
import { addApi } from 'shared/src/api'
import { UserFeed } from 'shared/src/api/types'
import {
  addDataLoadingFromPromise,
  isLoading,
  isError,
} from 'shared/src/utils/dataLoading'
import { branchIfPassesPredicate } from 'ad-hok-utils'

import { sansSerifFontFamily, serifFontFamily } from 'utils/styles'
import { FollowButton } from 'components/feed/profile/FollowButton'
import { Avatar } from 'components/Avatar'
import rodeo_u from 'assets/rodeo_u.svg'
import { LinkOrButton } from 'components/LinkOrButton'
import UserBadge from './UserBadge'

interface StyleProps {
  backgroundImageUrl?: string
}

const useStyles = createUseStyles({
  headerContainer: {
    marginTop: ({ backgroundImageUrl }: StyleProps) =>
      backgroundImageUrl ? 51 : 37,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    color: '#fff',
    gap: 9,
    minHeight: 267,
    background: ({ backgroundImageUrl }: StyleProps) =>
      backgroundImageUrl
        ? `url(${backgroundImageUrl}) no-repeat center center/cover`
        : null,
  },
  collegeBtn: {
    display: 'flex',
    flexDirection: 'row',
    gap: 5,
  },
  avatarPlaceholder: {
    height: 186,
  },
  usernameContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  pinUsernameToTop: {
    position: 'absolute',
    top: 10,
  },
  username: {
    fontFamily: serifFontFamily,
    fontWeight: 500,
    fontSize: 20,
    letterSpacing: 0.02,
  },
  bio: {
    fontFamily: sansSerifFontFamily,
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '16.8px',
    '& > p': {
      margin: 0,
    },
    margin: 0,
    maxWidth: 320,
    display: '-webkit-box',
    '-webkit-line-clamp': 4,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textAlign: 'center',
  },
  followButton: {
    margin: 'none',
  },
})

interface Props {
  feed: UserFeed
}

export const Header: FC<Props> = flowMax(
  addDisplayName('Header'),
  addProps(({ feed: { user } }) => ({
    bioParagraphs: user.bio?.split(/\r?\n/) ?? [],
    username: user.username,
  })),
  addProps(({ feed: { user } }) => ({
    classes: useStyles({ backgroundImageUrl: user.backgroundImage?.full }),
  })),
  addApi,
  addDataLoadingFromPromise(
    'collegeData',
    ({
      feed: {
        user: { collegeId },
      },
      api,
    }) =>
      collegeId ? api.colleges.get(collegeId) : Promise.resolve(undefined),
    ['feed'],
    ({
      feed: {
        user: { collegeId },
      },
    }) => `college-${collegeId}`
  ),
  branchIfPassesPredicate('collegeData', isLoading),
  branchIfPassesPredicate('collegeData', isError),
  addProps(({ collegeData }) => ({
    college: collegeData.data,
  })),
  ({ feed: { user }, bioParagraphs, username, classes, college }) => (
    <div className={classes.headerContainer}>
      {user.backgroundImage ? (
        <div className={classes.avatarPlaceholder} />
      ) : (
        <Avatar creator={user} height={140} noBadges={true} />
      )}
      <div
        className={cx(
          classes.usernameContainer,
          user.backgroundImage && classes.pinUsernameToTop
        )}
      >
        <span className={classes.username}>{username}</span>
        <UserBadge statusLevel={user.statusLevel} />
      </div>
      {bioParagraphs.map((paragraph, index) => (
        <p className={classes.bio} key={index}>
          {paragraph}
        </p>
      ))}
      {user.collegeId && (
        <div
          style={{
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          {!college ? (
            <div
              style={{
                width: 80,
                height: 24,
              }}
            />
          ) : (
            <LinkOrButton
              navigateTo={`/colleges/${college.shortname}`}
              className={classes.collegeBtn}
              showBackButton
            >
              <span
                style={{
                  fontSize: 16,
                  letterSpacing: 0.2,
                  textAlign: 'center',
                  color: '#FF8A00',
                  opacity: 0.6,
                }}
              >
                @{college.shortname}
              </span>
              <img
                src={rodeo_u}
                alt="rodeoU member"
                style={{ width: 18, height: 18 }}
              />
            </LinkOrButton>
          )}
        </div>
      )}
      <FollowButton creatorId={user.id} className={classes.followButton} />
    </div>
  )
)
